import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../button/button';
import type { OverlayScrollDetectionType } from '../../common/overlay-panel/overlay-panel.props';
import type { TypeOrArray } from '../../common-interfaces';
import { convertComparisonTypeToString } from '../../continuous-scroller/continuous-scroller-functions';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import DropDown from '../../fields/drop-down/drop-down.component';
import { isDropDownResult } from '../../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../../fields/drop-down/drop-down-types';
import Input from '../../fields/input/input.component';
import type { JsonData } from '../../generic-types';
import { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/popover';
import type { DataGridColumnSettings } from '../data-grid-types';
import { ApplyFilterButton, ClearFilterButton } from './common/filter-buttons.component';
import FilterChip from './common/filter-chip.component';

export const comparisonDropDownData: JsonData = [
  { value: ContinuousScrollerComparisonType.Equals, text: 'Equals' },
  { value: ContinuousScrollerComparisonType.GreaterThanEqualTo, text: 'Greater than or equal to' },
  { value: ContinuousScrollerComparisonType.LessThanEqualTo, text: 'Less than or equal to' },
  { value: ContinuousScrollerComparisonType.Between, text: 'Between' },
];

export function resolveComparisonDisplayValue(value: ContinuousScrollerComparisonType) {
  const appliedFilter = comparisonDropDownData.find((item) => item.value === value);
  if (appliedFilter) {
    return appliedFilter.text;
  }
  return convertComparisonTypeToString(value);
}

const defaultComparisonValue = {
  value: ContinuousScrollerComparisonType.Equals,
  text: 'Equals',
};

interface DataGridNumericFilterPanelProps {
  column: DataGridColumnSettings;
  currentValue: string | Array<string>;
  scrollDetection?: OverlayScrollDetectionType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilterChange: (filter: ContinuousScrollerFilter) => void;
  onClearFilters: () => void;
  onHidePanel?: () => void;
  displayValue?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

function DataGridNumericFilterPanel({
  column,
  currentValue,
  scrollDetection,
  open,
  onOpenChange,
  onFilterChange,
  onClearFilters,
  onHidePanel,
  displayValue,
  side = 'bottom',
  align = 'start',
}: DataGridNumericFilterPanelProps) {
  const initialComparison: DropDownResult = column.searchComparison
    ? (() => {
        const found = comparisonDropDownData.find((c) => c.value === column.searchComparison);
        return found && isDropDownResult(found) ? found : defaultComparisonValue;
      })()
    : defaultComparisonValue;

  const [selectedComparisonValue, setSelectedComparisonValue] = useState<DropDownResult>(initialComparison);
  const [minimumValue, setMinimumValue] = useState(() => {
    if (currentValue.length === 0) return '';
    if (Array.isArray(currentValue) && currentValue.length === 2) return currentValue[0];
    if (!Array.isArray(currentValue)) {
      if (
        initialComparison.value === ContinuousScrollerComparisonType.Between &&
        currentValue.split(',').length === 2
      ) {
        return currentValue.split(',')[0];
      }
      return currentValue;
    }
    return '';
  });
  const [maximumValue, setMaximumValue] = useState(() => {
    if (currentValue.length === 0) return '';
    if (Array.isArray(currentValue) && currentValue.length === 2) return currentValue[1];
    if (
      !Array.isArray(currentValue) &&
      initialComparison.value === ContinuousScrollerComparisonType.Between &&
      currentValue.split(',').length === 2
    ) {
      return currentValue.split(',')[1];
    }
    return '';
  });

  const { t } = useTranslation();

  const applyFilters = useCallback(
    (value: string, secondValue: string) => {
      if (
        selectedComparisonValue.value === ContinuousScrollerComparisonType.Between &&
        (value.length === 0 || secondValue.length === 0)
      ) {
        return;
      }

      // column.id is Key (string | number | symbol); ContinuousScrollerFilter only supports string | number.
      let resolvedId: string | number;
      if (typeof column.id === 'symbol') {
        const sym: symbol = column.id;
        resolvedId = sym.description ?? String(sym);
      } else {
        resolvedId = column.id;
      }
      const filter: ContinuousScrollerFilter = {
        scrollerId: resolvedId,
        field: column.field,
        customDataProperties: column.customDataProperties,
        value: '',
      };

      if (value.length > 0) {
        switch (selectedComparisonValue.value) {
          case ContinuousScrollerComparisonType.Between:
            filter.comparison = ContinuousScrollerComparisonType.Between;
            filter.value = [value, secondValue];
            break;
          case ContinuousScrollerComparisonType.GreaterThanEqualTo:
            filter.comparison = ContinuousScrollerComparisonType.GreaterThanEqualTo;
            filter.value = value;
            break;
          case ContinuousScrollerComparisonType.LessThanEqualTo:
            filter.comparison = ContinuousScrollerComparisonType.LessThanEqualTo;
            filter.value = value;
            break;
          case ContinuousScrollerComparisonType.Equals:
            filter.comparison = ContinuousScrollerComparisonType.Equals;
            filter.value = value;
            break;
        }
      }

      onFilterChange(filter);
    },
    [column.id, column.field, column.customDataProperties, selectedComparisonValue.value, onFilterChange]
  );

  const resetFilters = () => {
    onClearFilters();
    setMaximumValue('');
    setMinimumValue('');
  };

  const onMinimumValueChange = (value: string | undefined) => {
    const newMinimumValue = value ?? '';
    setMinimumValue(newMinimumValue);
  };

  const onMaximumValueChange = (value: string | undefined) => {
    const newMaximumValue = value ?? '';
    setMaximumValue(newMaximumValue);
  };

  const onComparisonValueChange = (value: TypeOrArray<DropDownResult> | DropDownNoSelection | undefined) => {
    if (isDropDownResult(value)) {
      resetFilters();
      setSelectedComparisonValue(value);
    }
  };

  const maxValueError = useMemo(() => {
    if (selectedComparisonValue.value === ContinuousScrollerComparisonType.Between) {
      if (minimumValue.length === 0 || maximumValue.length === 0) {
        return undefined;
      }

      const maxVal = parseFloat(maximumValue);
      const minVal = parseFloat(minimumValue);
      if (Number.isNaN(maxVal) || Number.isNaN(minVal)) {
        return undefined;
      }

      if (maxVal < minVal) {
        return t('max-value-error');
      }
    }
    return undefined;
  }, [minimumValue, maximumValue, selectedComparisonValue.value, t]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onHidePanel?.();
    }
  };

  const handleApplyClick = () => {
    if (maxValueError) return;
    applyFilters(minimumValue, maximumValue);
    onHidePanel?.();
  };

  const isApplyDisabled = () => {
    if (maxValueError) return true;
    if (selectedComparisonValue.value === ContinuousScrollerComparisonType.Between) {
      return minimumValue.length === 0 || maximumValue.length === 0;
    }
    return minimumValue.length === 0;
  };

  const handleFilterChipClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    resetFilters();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger
        nativeButton={false}
        render={
          <div className="flex h-full cursor-pointer items-center rounded-sm border border-gray-4 bg-mono-1 px-1">
            <FilterChip text={displayValue} onClear={handleFilterChipClear} />
            <IconButton icon="Filter" iconSize={OdinIconSize.ExtraSmall} />
          </div>
        }
      />
      <PopoverContent side={side} align={align} className="w-40">
        <DropDown
          data={comparisonDropDownData}
          textField="text"
          valueField="value"
          value={selectedComparisonValue}
          onChange={onComparisonValueChange}
          dropDownStyles="bg-primary-5 border-primary-1 rounded-full text-primary-1 px-2"
          optionSelectedStyle="bg-primary-5"
          optionHoverStyle="hover:bg-primary-5"
          allowSearch={false}
          allowClear={false}
          scrollDetection={scrollDetection}
        />
        <div className="flex flex-col gap-3 px-1 pt-3">
          <Input
            name="min"
            type="number"
            value={minimumValue}
            placeholder={
              selectedComparisonValue.value === ContinuousScrollerComparisonType.Between ? t('min-value') : t('value')
            }
            onChange={onMinimumValueChange}
          />
          {selectedComparisonValue.value === ContinuousScrollerComparisonType.Between && (
            <Input
              name="max"
              type="number"
              value={maximumValue}
              placeholder={t('max-value')}
              onChange={onMaximumValueChange}
              error={maxValueError}
            />
          )}
          <div className="flex items-center justify-start gap-2 pt-2">
            <ApplyFilterButton onClick={handleApplyClick} disabled={isApplyDisabled()} />
            <ClearFilterButton onClick={resetFilters} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DataGridNumericFilterPanel;
